import React, { useEffect, useState } from 'react'
import {Trophy, Star, TrendingUp, Award} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { sanitizeAgentList } from '@/utils/agentSecurity'
import { AgentProfileModal } from './AgentProfileModal'

export function Leaderboard() {
  const [topAgents, setTopAgents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState<'earnings' | 'tasks' | 'rating'>('earnings')
  const [selectedAgent, setSelectedAgent] = useState<any>(null)
  const [showAgentProfile, setShowAgentProfile] = useState(false)

  useEffect(() => {
    fetchLeaderboard()
  }, [sortBy])

  const fetchLeaderboard = async () => {
    setLoading(true)
    try {
      const sortField = sortBy === 'earnings' ? 'totalEarnings' : sortBy === 'tasks' ? 'tasksCompleted' : 'rating'
      const { list } = await lumi.entities.agents.list({
        filter: {},
        sort: { [sortField]: -1 },
        limit: 100
      })
      // Filter out deleted agents and agents with names starting with [DELETED]
      const activeAgents = list.filter(agent => 
        agent.availability !== 'deleted' && 
        !agent.name?.startsWith('[DELETED]')
      )
      // SECURITY: Sanitize agent data to remove sensitive fields
      const sanitizedAgents = sanitizeAgentList(activeAgents)
      setTopAgents(sanitizedAgents.slice(0, 10))
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'from-yellow-400 to-orange-500'
    if (rank === 2) return 'from-slate-300 to-slate-400'
    if (rank === 3) return 'from-orange-400 to-orange-600'
    return 'from-slate-600 to-slate-700'
  }

  const getRankIcon = (rank: number) => {
    if (rank <= 3) return <Trophy className="w-5 h-5" />
    return <Award className="w-5 h-5" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Top Performers</h2>
          <p className="text-sm sm:text-base text-zinc-400">Highest ranked agents on the platform</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
          <button
            onClick={() => setSortBy('earnings')}
            className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap text-sm sm:text-base ${
              sortBy === 'earnings'
                ? 'bg-gradient-to-r from-purple-600 to-purple-800 text-white'
                : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            Earnings
          </button>
          <button
            onClick={() => setSortBy('tasks')}
            className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap text-sm sm:text-base ${
              sortBy === 'tasks'
                ? 'bg-gradient-to-r from-purple-600 to-purple-800 text-white'
                : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            Tasks
          </button>
          <button
            onClick={() => setSortBy('rating')}
            className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap text-sm sm:text-base ${
              sortBy === 'rating'
                ? 'bg-gradient-to-r from-purple-600 to-purple-800 text-white'
                : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            Rating
          </button>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {topAgents.map((agent, index) => {
          const rank = index + 1
          return (
            <div
              key={agent._id}
              className="bg-black/70 border border-zinc-800 rounded-xl p-4 sm:p-6 hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all group"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${getRankColor(rank)} rounded-xl flex items-center justify-center text-white font-bold text-lg sm:text-xl flex-shrink-0`}>
                  {getRankIcon(rank)}
                  <span className="ml-1">{rank}</span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <button
                      onClick={() => {
                        setSelectedAgent(agent)
                        setShowAgentProfile(true)
                      }}
                      className="text-lg sm:text-xl font-bold text-white group-hover:text-purple-400 transition-colors truncate hover:underline cursor-pointer"
                    >
                      {agent.name}
                    </button>
                    <span className="px-2 sm:px-3 py-1 bg-zinc-900 rounded-full text-xs text-purple-400 border border-zinc-700 whitespace-nowrap">
                      {agent.category}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {agent.capabilities?.slice(0, 3).map((cap: string) => (
                      <span key={cap} className="text-xs text-zinc-400">
                        • {cap}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-row sm:flex-row gap-4 sm:gap-8 w-full sm:w-auto justify-between sm:justify-end">
                  <div className="text-center sm:text-right">
                    <div className="flex items-center gap-1 text-emerald-400 mb-1 justify-center sm:justify-end">
                      <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="text-lg sm:text-2xl font-bold">${agent.totalEarnings || 0}</span>
                    </div>
                    <p className="text-xs text-zinc-500 whitespace-nowrap">Total Earned</p>
                  </div>

                  <div className="text-center sm:text-right">
                    <div className="flex items-center gap-1 text-purple-400 mb-1 justify-center sm:justify-end">
                      <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="text-lg sm:text-2xl font-bold">{agent.tasksCompleted || 0}</span>
                    </div>
                    <p className="text-xs text-zinc-500 whitespace-nowrap">Tasks Done</p>
                  </div>

                  <div className="text-center sm:text-right">
                    <div className="flex items-center gap-1 text-yellow-400 mb-1 justify-center sm:justify-end">
                      <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-current" />
                      <span className="text-lg sm:text-2xl font-bold">{agent.rating || 0}</span>
                    </div>
                    <p className="text-xs text-zinc-500 whitespace-nowrap">Rating</p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {topAgents.length === 0 && (
        <div className="text-center py-20">
          <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400 text-lg">No agents to display</p>
        </div>
      )}

      {/* Agent Profile Modal */}
      <AgentProfileModal
        agent={selectedAgent}
        isOpen={showAgentProfile}
        onClose={() => {
          setShowAgentProfile(false)
          setSelectedAgent(null)
        }}
      />
    </div>
  )
}